import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import CalendarTodayIcon   from '@mui/icons-material/CalendarToday';
import ExpandLessIcon      from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon      from '@mui/icons-material/ExpandMore';
import PauseIcon           from '@mui/icons-material/Pause';
import PlayArrowIcon       from '@mui/icons-material/PlayArrow';
import ReplayIcon          from '@mui/icons-material/Replay';
import TableChartIcon      from '@mui/icons-material/TableChart';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import * as imCoverageApi  from '../services/imCoverageApi';
import * as imBackfillApi  from '../services/imBackfillApi';
import { useAuth }         from '../contexts/AuthContext';

// ─── Constants ────────────────────────────────────────────────────────────────

const MONTH_NAMES  = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const WEEK_DAYS    = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const TYPE_COLORS = {
  W:   '#1976d2',
  B:   '#388e3c',
  P:   '#f57c00',
  BIZ: '#7b1fa2',
  WA:  '#0097a7',
};

const TYPE_LABELS = {
  W:   'Direct Enquiry',
  B:   'Buy Lead',
  P:   'PNS Call',
  BIZ: 'Catalog View',
  WA:  'WhatsApp',
};

// Colour levels for day circles (relative to month max count)
const LEVEL_COLORS = ['#c8f5c5', '#81c784', '#388e3c', '#1b5e20'];
const LEVEL_TEXT   = ['#1a1a1a', '#1a1a1a', '#fff', '#fff'];

function dayLevel(count, maxCount) {
  if (!count || !maxCount) return -1;
  const ratio = count / maxCount;
  if (ratio <= 0.25) return 0;
  if (ratio <= 0.50) return 1;
  if (ratio <= 0.75) return 2;
  return 3;
}

function todayStr() {
  return new Date().toLocaleString('en-CA', { timeZone: 'Asia/Kolkata' }).split(',')[0];
}

// ─── KPI Strip ────────────────────────────────────────────────────────────────

function KPIStrip({ totals, year, yearLoading }) {
  if (yearLoading) {
    return (
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {[...Array(4)].map((_, i) => (
          <Skeleton key={i} variant="rounded" width={160} height={72} />
        ))}
      </Box>
    );
  }
  if (!totals) return null;

  const items = [
    { label: 'Total Leads', value: totals.total?.toLocaleString() ?? '—', color: 'primary.main' },
    { label: 'Best Month', value: totals.bestMonth ? MONTH_NAMES[totals.bestMonth - 1] : '—', sub: totals.bestMonthTotal ? `${totals.bestMonthTotal.toLocaleString()} leads` : '', color: 'success.main' },
    { label: 'Best Day', value: totals.bestDay ?? '—', sub: totals.bestDayTotal ? `${totals.bestDayTotal} leads` : '', color: 'secondary.main' },
    { label: 'Missed Days', value: totals.missedDays?.toLocaleString() ?? '—', color: totals.missedDays > 0 ? 'error.main' : 'text.secondary' },
  ];

  return (
    <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
      {items.map((item) => (
        <Paper key={item.label} elevation={0} variant="outlined" sx={{ px: 2.5, py: 1.5, minWidth: 140, borderRadius: 2 }}>
          <Typography variant="caption" color="text.secondary" display="block">{item.label}</Typography>
          <Typography variant="h6" sx={{ color: item.color, lineHeight: 1.2 }}>{item.value}</Typography>
          {item.sub && <Typography variant="caption" color="text.secondary">{item.sub}</Typography>}
        </Paper>
      ))}
    </Box>
  );
}

// ─── Year Line Chart ──────────────────────────────────────────────────────────

function YearLineChart({ months }) {
  if (!months || months.length === 0) return null;
  const data = months.map((m) => ({ name: MONTH_NAMES[m.month - 1], leads: m.total }));
  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent sx={{ pb: '16px !important' }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Monthly Trend</Typography>
        <ResponsiveContainer width="100%" height={160}>
          <LineChart data={data} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} width={36} />
            <RechartsTooltip />
            <Line type="monotone" dataKey="leads" stroke="#1976d2" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

// ─── Calendar View ────────────────────────────────────────────────────────────

function CalendarView({ days, year, month }) {
  if (!days || days.length === 0) return null;

  const today = todayStr();
  const maxCount = Math.max(...days.map((d) => d.total), 1);
  const firstDow = new Date(year, month - 1, 1).getDay(); // 0=Sun
  const cells = [...Array(firstDow).fill(null), ...days];

  return (
    // Cap width so the grid doesn't spread across the full page.
    // Headers and cells share the same column template inside this container.
    <Box sx={{ maxWidth: { xs: '320px', sm: '360px' } }}>
      {/* Day-of-week headers — same grid as cells so columns stay aligned */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 0.5 }}>
        {WEEK_DAYS.map((d) => (
          <Typography
            key={d}
            align="center"
            color="text.secondary"
            sx={{ fontWeight: 600, fontSize: { xs: '0.6875rem', sm: '0.75rem' }, lineHeight: '22px' }}
          >
            {d}
          </Typography>
        ))}
      </Box>

      {/* Day cells — each cell is a flex centring wrapper; circle has a fixed size */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '8px' }}>
        {cells.map((day, idx) => {
          if (!day) return <Box key={`empty-${idx}`} sx={{ height: { xs: 4.25, sm: 5 } }} />;

          const level    = dayLevel(day.total, maxCount);
          const isToday  = day.date === today;
          const isPast   = day.date < today;
          const isMissed = day.total === 0 && isPast;

          const bg    = level >= 0 ? LEVEL_COLORS[level] : 'transparent';
          const color = level >= 0 ? LEVEL_TEXT[level]   : 'text.disabled';

          const tooltipLines = [
            day.date,
            `Total: ${day.total}`,
            ...Object.entries(day.byType).map(([t, c]) => `${TYPE_LABELS[t] || t}: ${c}`),
          ].join('\n');

          return (
            // Outer Box is the grid cell — centres the fixed-size circle
            <Box key={day.date} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{tooltipLines}</span>} arrow>
                <Box
                  sx={{
                    // Fixed size: 4.25 = 34 px (mobile), 5 = 40 px (desktop) via MUI spacing
                    width:  { xs: 4.25, sm: 5 },
                    height: { xs: 4.25, sm: 5 },
                    flexShrink: 0,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: bg,
                    color: color,
                    fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                    fontWeight: 600,
                    cursor: 'default',
                    border: isToday ? '1.5px solid' : isMissed ? '1.5px dashed' : 'none',
                    borderColor: isToday ? 'primary.main' : isMissed ? 'error.light' : 'transparent',
                    opacity: !isPast && day.total === 0 ? 0.4 : 1,
                  }}
                >
                  {day.date.split('-')[2]}
                </Box>
              </Tooltip>
            </Box>
          );
        })}
      </Box>

      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
        {LEVEL_COLORS.map((c, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: c }} />
            <Typography variant="caption" color="text.secondary">
              {['1–25%', '26–50%', '51–75%', '76–100%'][i]}
            </Typography>
          </Box>
        ))}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', border: '1.5px dashed', borderColor: 'error.light' }} />
          <Typography variant="caption" color="text.secondary">Missed</Typography>
        </Box>
      </Box>
    </Box>
  );
}

// ─── Backfill Card ────────────────────────────────────────────────────────────

const WIN_STATUS_COLORS = {
  pending:              'default',
  done:                 'success',
  failed:               'error',
  skipped_out_of_range: 'warning',
};

function BackfillCard({ onWindowComplete }) {
  const { isAdmin } = useAuth();
  const [data,          setData]          = useState(null);
  const [fetchError,    setFetchError]    = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError,   setActionError]   = useState(null);
  const [showWindows,   setShowWindows]   = useState(false);
  const prevDoneRef = useRef(0);

  const refresh = useCallback(async () => {
    try {
      const result = await imBackfillApi.getBackfillStatus();
      const newDone = result?.progress?.done ?? 0;
      if (newDone > prevDoneRef.current) onWindowComplete?.();
      prevDoneRef.current = newDone;
      setData(result);
      setFetchError(null);
    } catch (err) {
      setFetchError(err.message);
    }
  }, [onWindowComplete]);

  useEffect(() => { refresh(); }, [refresh]);

  // Poll every 15 s while running
  useEffect(() => {
    if (data?.status !== 'running') return;
    const id = setInterval(refresh, 15_000);
    return () => clearInterval(id);
  }, [data?.status, refresh]);

  const act = async (fn) => {
    setActionLoading(true);
    setActionError(null);
    try { await fn(); await refresh(); }
    catch (err) { setActionError(err.message); }
    finally { setActionLoading(false); }
  };

  const fmtDate = (str) => (str ? str.slice(0, 10) : '?');
  const fmtWindow = (w) => `${fmtDate(w?.start)} → ${fmtDate(w?.end)}`;
  const fmtEta = (min) => {
    if (!min) return null;
    const h = Math.floor(min / 60), m = min % 60;
    return h > 0 ? `~${h}h ${m > 0 ? `${m}m` : ''}`.trim() : `~${m}m`;
  };

  if (fetchError) {
    return (
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Historical Backfill</Typography>
          <Alert severity="error">{fetchError}</Alert>
        </CardContent>
      </Card>
    );
  }

  if (!data) return <Skeleton variant="rounded" height={100} sx={{ mb: 3 }} />;

  const { status, progress, currentWindow, etaMin, totalInserted, totalUpdated, totalFetched, lastError, windows } = data;
  const { done = 0, total = 0, failed = 0, pct = 0 } = progress || {};
  const isRunning = status === 'running';

  const statusColor = { running: 'primary', completed: 'success', paused: 'warning', failed: 'error' }[status] || 'default';

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle2">Historical Backfill</Typography>
          {status && <Chip label={status} size="small" color={statusColor} />}
        </Box>

        {!data.exists ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Fetch all IndiaMART leads from 26 Oct 2025 to today. One-time import — supports pause and resume.
          </Typography>
        ) : (
          <>
            {/* Progress bar */}
            {total > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {done}/{total} windows ({pct}%)
                  </Typography>
                  {etaMin && (
                    <Typography variant="caption" color="text.secondary">ETA {fmtEta(etaMin)}</Typography>
                  )}
                </Box>
                <LinearProgress variant="determinate" value={pct} sx={{ borderRadius: 1 }} />
              </Box>
            )}

            {/* Current window */}
            {isRunning && currentWindow && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Now: {fmtWindow(currentWindow)}
              </Typography>
            )}

            {/* Counts */}
            <Box sx={{ display: 'flex', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
              <Typography variant="caption">Fetched: <b>{(totalFetched || 0).toLocaleString()}</b></Typography>
              <Typography variant="caption">New: <b>{(totalInserted || 0).toLocaleString()}</b></Typography>
              {(totalUpdated || 0) > 0 && (
                <Typography variant="caption" color="text.secondary">Updated: <b>{totalUpdated.toLocaleString()}</b></Typography>
              )}
              {failed > 0 && (
                <Typography variant="caption" color="error.main">Failed: <b>{failed}</b></Typography>
              )}
            </Box>

            {/* Last error */}
            {lastError && (
              <Alert severity="warning" sx={{ mb: 1.5, py: 0 }}>
                <Typography variant="caption">{lastError}</Typography>
              </Alert>
            )}
          </>
        )}

        {/* Action error */}
        {actionError && (
          <Alert severity="error" sx={{ mb: 1.5, py: 0 }}>
            <Typography variant="caption">{actionError}</Typography>
          </Alert>
        )}

        {/* Buttons */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          {isAdmin && (
            <>
              {(!data.exists || status === 'idle' || status === 'completed') && (
                <Button size="small" variant="contained" startIcon={<PlayArrowIcon />}
                  onClick={() => act(imBackfillApi.startBackfill)} disabled={actionLoading}>
                  {status === 'completed' ? 'Restart' : 'Start'}
                </Button>
              )}
              {isRunning && (
                <Button size="small" variant="outlined" startIcon={<PauseIcon />}
                  onClick={() => act(imBackfillApi.pauseBackfill)} disabled={actionLoading}>
                  Pause
                </Button>
              )}
              {status === 'paused' && (
                <Button size="small" variant="contained" startIcon={<PlayArrowIcon />}
                  onClick={() => act(imBackfillApi.resumeBackfill)} disabled={actionLoading}>
                  Resume
                </Button>
              )}
              {failed > 0 && !isRunning && (
                <Button size="small" variant="outlined" color="warning" startIcon={<ReplayIcon />}
                  onClick={() => act(imBackfillApi.retryFailedWindows)} disabled={actionLoading}>
                  Retry Failed ({failed})
                </Button>
              )}
            </>
          )}

          {data.exists && windows?.length > 0 && (
            <Button size="small" variant="text" color="inherit"
              endIcon={showWindows ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={() => setShowWindows((v) => !v)}
              sx={{ ml: 'auto', textTransform: 'none', fontSize: '0.75rem' }}>
              {showWindows ? 'Hide' : 'Show'} Windows
            </Button>
          )}
        </Box>

        {/* Collapsible window list */}
        <Collapse in={showWindows && !!windows?.length}>
          <Box sx={{ mt: 1.5, maxHeight: 280, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            {(windows || []).map((w, i) => (
              <Box key={i} sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                py: 0.5, px: 1,
                borderBottom: '1px solid', borderColor: 'divider',
                '&:last-child': { borderBottom: 'none' },
                bgcolor: i % 2 === 0 ? 'transparent' : 'action.hover',
              }}>
                <Typography variant="caption" sx={{ minWidth: 160, fontFamily: 'monospace', flexShrink: 0 }}>
                  {fmtWindow(w)}
                </Typography>
                <Chip label={w.status} size="small" color={WIN_STATUS_COLORS[w.status] || 'default'}
                  sx={{ fontSize: '0.625rem', height: 18, flexShrink: 0 }} />
                {w.status === 'done' && (
                  <Typography variant="caption" color="text.secondary">
                    {w.fetched > 0 ? `${w.fetched} fetched` : '0 fetched'}
                    {w.inserted > 0 ? ` / +${w.inserted} new` : ''}
                    {w.updated > 0 ? ` / ${w.updated} updated` : ''}
                    {w.fetched > 0 && w.inserted === 0 && (w.updated || 0) === 0 ? ' / 0 saved ⚠' : ''}
                  </Typography>
                )}
                {w.status === 'failed' && w.error && (
                  <Typography variant="caption" color="error.main"
                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>
                    {w.error}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}

// ─── Rows View ────────────────────────────────────────────────────────────────

function RowsView({ days }) {
  if (!days || days.length === 0) return null;
  const today = todayStr();

  const chartData = days
    .filter((d) => d.total > 0)
    .map((d) => ({ name: d.date.split('-')[2], ...d.byType }));

  const allTypes = [...new Set(days.flatMap((d) => Object.keys(d.byType)))];

  return (
    <Box>
      {chartData.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <ResponsiveContainer width="100%" height={140}>
            <BarChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={1} />
              <YAxis tick={{ fontSize: 10 }} width={28} />
              <RechartsTooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {allTypes.map((t) => (
                <Bar key={t} dataKey={t} name={TYPE_LABELS[t] || t} stackId="a" fill={TYPE_COLORS[t] || '#90a4ae'} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}

      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell align="right">Total</TableCell>
              {allTypes.map((t) => (
                <TableCell key={t} align="right" sx={{ color: TYPE_COLORS[t] || 'text.primary' }}>
                  {TYPE_LABELS[t] || t}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {days.map((day) => {
              const isMissed = day.total === 0 && day.date < today;
              return (
                <TableRow
                  key={day.date}
                  sx={{ bgcolor: isMissed ? 'error.50' : day.date === today ? 'primary.50' : undefined }}
                >
                  <TableCell sx={{ color: isMissed ? 'error.main' : 'text.primary' }}>
                    {day.date}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: day.total > 0 ? 600 : 400 }}>
                    {day.total || '—'}
                  </TableCell>
                  {allTypes.map((t) => (
                    <TableCell key={t} align="right" sx={{ color: 'text.secondary' }}>
                      {day.byType[t] || '—'}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ─── Month Section ────────────────────────────────────────────────────────────

function MonthSection({ monthSummary, year, isOpen, onToggle, cachedData, onLoad }) {
  const [view, setView] = useState('calendar');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const loadedRef = useRef(false);

  const loadData = useCallback(async () => {
    if (cachedData || loadedRef.current) return;
    loadedRef.current = true;
    setLoading(true);
    setError(null);
    try {
      await onLoad(year, monthSummary.month);
    } catch (err) {
      setError(err.message);
      loadedRef.current = false;
    } finally {
      setLoading(false);
    }
  }, [cachedData, year, monthSummary.month, onLoad]);

  useEffect(() => {
    if (isOpen && !cachedData && !loading) loadData();
  }, [isOpen, cachedData, loading, loadData]);

  const days = cachedData?.days || [];

  return (
    <Card variant="outlined" sx={{ mb: 1.5 }}>
      {/* Month header (always visible) */}
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2, py: 1.25, cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography sx={{ fontWeight: 600, minWidth: 96 }}>
            {monthSummary.name}
          </Typography>
          <Chip label={monthSummary.total.toLocaleString()} size="small"
            color={monthSummary.total > 0 ? 'primary' : 'default'} variant="outlined" />
          <Typography variant="caption" color="text.secondary">
            {monthSummary.activeDays} active · {monthSummary.missedDays} missed
          </Typography>
        </Box>
        {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </Box>

      <Collapse in={isOpen}>
        <Box sx={{ px: 2, pb: 2 }}>
          {/* View toggle */}
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
            <Button
              size="small" variant={view === 'calendar' ? 'contained' : 'outlined'}
              startIcon={<CalendarTodayIcon fontSize="small" />}
              onClick={() => setView('calendar')}
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
              Calendar
            </Button>
            <Button
              size="small" variant={view === 'rows' ? 'contained' : 'outlined'}
              startIcon={<TableChartIcon fontSize="small" />}
              onClick={() => setView('rows')}
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
              Rows
            </Button>
          </Box>

          {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={28} /></Box>}
          {error   && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

          {!loading && !error && days.length > 0 && (
            view === 'calendar'
              ? <CalendarView days={days} year={year} month={monthSummary.month} />
              : <RowsView days={days} />
          )}
        </Box>
      </Collapse>
    </Card>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function IndiamartCoverage() {
  const currentYear = new Date().getFullYear();
  const [year,        setYear]        = useState(currentYear);
  const [yearData,    setYearData]    = useState(null);
  const [yearLoading, setYearLoading] = useState(false);
  const [yearError,   setYearError]   = useState(null);
  const [openMonths,  setOpenMonths]  = useState(new Set());
  const [monthCache,  setMonthCache]  = useState({}); // key: "year-month"
  const [yearRange,   setYearRange]   = useState({ min: currentYear, max: currentYear });

  const loadYear = useCallback(async (y) => {
    setYearLoading(true);
    setYearError(null);
    setYearData(null);
    try {
      const result = await imCoverageApi.getYearCoverage(y);
      setYearData(result.data);
      if (result.data.yearRange) setYearRange(result.data.yearRange);
    } catch (err) {
      setYearError(err.message);
    } finally {
      setYearLoading(false);
    }
  }, []);

  useEffect(() => { loadYear(year); }, [year, loadYear]);
  // Clear month cache when year changes
  useEffect(() => { setMonthCache({}); setOpenMonths(new Set()); }, [year]);

  const handleMonthLoad = useCallback(async (y, month) => {
    const key = `${y}-${month}`;
    const result = await imCoverageApi.getMonthCoverage(y, month);
    setMonthCache((prev) => ({ ...prev, [key]: result.data }));
  }, []);

  const toggleMonth = (month) => {
    setOpenMonths((prev) => {
      const next = new Set(prev);
      if (next.has(month)) { next.delete(month); } else { next.add(month); }
      return next;
    });
  };

  // Backfill: when a window completes, clear the month cache so re-opening a month shows fresh data
  const handleBackfillWindowComplete = useCallback(() => {
    setMonthCache({});
  }, []);

  const yearOptions = [];
  for (let y = yearRange.max; y >= yearRange.min; y--) yearOptions.push(y);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 }, maxWidth: 900 }}>
      {/* Page header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">IndiaMART Coverage</Typography>
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel>Year</InputLabel>
          <Select value={year} label="Year" onChange={(e) => setYear(e.target.value)}>
            {yearOptions.map((y) => (
              <MenuItem key={y} value={y}>{y}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {yearError && <Alert severity="error" sx={{ mb: 2 }}>{yearError}</Alert>}

      {/* KPI strip */}
      <KPIStrip totals={yearData?.totals} year={year} yearLoading={yearLoading} />

      {/* Year trend chart */}
      {yearLoading && <Skeleton variant="rounded" height={196} sx={{ mb: 3 }} />}
      {!yearLoading && yearData && <YearLineChart months={yearData.months} />}

      {/* Backfill card */}
      <BackfillCard onWindowComplete={handleBackfillWindowComplete} />

      {/* 12 month sections */}
      {yearLoading && (
        <Box>
          {[...Array(3)].map((_, i) => <Skeleton key={i} variant="rounded" height={52} sx={{ mb: 1.5 }} />)}
        </Box>
      )}

      {!yearLoading && yearData?.months?.map((monthSummary) => {
        const cacheKey = `${year}-${monthSummary.month}`;
        return (
          <MonthSection
            key={monthSummary.month}
            monthSummary={monthSummary}
            year={year}
            isOpen={openMonths.has(monthSummary.month)}
            onToggle={() => toggleMonth(monthSummary.month)}
            cachedData={monthCache[cacheKey] || null}
            onLoad={handleMonthLoad}
          />
        );
      })}
    </Box>
  );
}
